import React from 'react';
import { Badge, Tag, Popover, Tooltip, Button } from "antd";
import moment from 'moment';
import { Link } from 'lucide-react';

export const singleLegSignalTypes = [
	{ label: 'Long Entry', value: 'LE' },
	{ label: 'Long Exit', value: 'LX' },
	{ label: 'Short Entry', value: 'SE' },
	{ label: 'Short Exit', value: 'SX' },
];

export const multiLegSignalTypes = [
	{ label: 'Entry', value: 'E' },
	{ label: 'Exit', value: 'X' },
];

export const getSignalTypeLabel = (value) => {
	const type = singleLegSignalTypes.concat(multiLegSignalTypes).find(t => t.value === value);
		return type ? type.label : value;
};

export const StatusBadge = ({ status }) => {
  const map = {
    0: { text: "Stopped", color: "red" },
    1: { text: "Monitoring", color: "yellow" },
    2: { text: "Active", color: "green" },
  };
  const m = map[status] || { text: String(status), color: "default" };
  return <Badge color={m.color} text={m.text} />;
};

export const SignalNamesDisplay = ({ signalLinks, onIconClick, record }) => {
  if (!Array.isArray(signalLinks) || signalLinks.length === 0) {
    return <span style={{ opacity: 0.5 }}>-</span>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Tooltip title={`View ${signalLinks.length} Linked Signal${signalLinks.length > 1 ? 's' : ''}`}>
        <Button
          size="small"
          icon={<Link size={14} />}
          onClick={(e) => {
            e.stopPropagation();
            if (onIconClick) onIconClick(signalLinks, record);
          }} 
        />
      </Tooltip>
    </div>
  );
};



export const BrokerAccountsDisplay = ({ brokers, labelFn }) => {
  if (!Array.isArray(brokers) || brokers.length === 0) {
    return <Tag color="yellow">N/A</Tag>;
  }

  const getBrokerId = (broker) => typeof broker === 'object' ? broker.id : broker;
  const getMultiplier = (broker) => typeof broker === 'object' ? broker.multiplier : 1;

  if (brokers.length === 1) {
    const brokerId = getBrokerId(brokers[0]);
    const multiplier = getMultiplier(brokers[0]);
    return (
      <Tag color="purple">
        {labelFn ? labelFn(brokerId) : brokerId}
        <span className="broker-multiplier-badge">
          (x{multiplier})
        </span>
      </Tag>
    );
  }

  const content = (
    <div style={{ maxWidth: 300 }}>
      {brokers.map((broker, index) => {
        const brokerId = getBrokerId(broker);
        const multiplier = getMultiplier(broker);
        return (
          <div key={index} style={{ padding: '4px 0' }}>
            <Tag color="purple">
              {labelFn ? labelFn(brokerId) : brokerId}
              <span className="broker-multiplier-badge">
                (x{multiplier})
              </span>
            </Tag>
          </div>
        );
      })}
    </div>
  );

  const firstBrokerId = getBrokerId(brokers[0]);
  const firstMultiplier = getMultiplier(brokers[0]);

  return (
    <Popover content={content} title="Linked Accounts" trigger="hover" placement="top">
      <Tag color="purple" style={{ cursor: 'pointer' }}>
        {labelFn ? labelFn(firstBrokerId) : firstBrokerId}
        <span className="broker-multiplier-badge">
          (x{firstMultiplier})
        </span>
        <span className="broker-more-badge">
          +{brokers.length - 1} more
        </span>
      </Tag>
    </Popover>
  );
};

export const getBrokerLabelUtil = (idOrObj, brokersList) => {
    const id = (idOrObj && typeof idOrObj === 'object') ? idOrObj?.id : idOrObj;
    const b = (brokersList || []).find(x => (x?.id?.toString?.() || '') === (id?.toString?.() || ''));
    if (!b) return id;
    return b?.name?.toUpperCase() || `${b?.type?.toUpperCase?.()} - ${b?.userName?.toUpperCase?.()}` || id;
};

export const getDisplayNumber = (value) => {
  if (value === 0) return 0;
  return value ?? '-';
};

export const strategyTemplates = [
  {
    group: "Bullish",
    templates: [
      { label: "Buy Call", value: "Buy Call" },
      { label: "Sell Put", value: "Sell Put" },
      { label: "Bull Call Spread", value: "Bull Call Spread" },
      { label: "Bull Put Spread", value: "Bull Put Spread" },
      { label: "Call Ratio Back Spread", value: "Call Ratio Back Spread" },
      { label: "Long Calendar with Calls", value: "Long Calendar with Calls" },
      { label: "Bull Condor", value: "Bull Condor" },
      { label: "Bull Butterfly", value: "Bull Butterfly" },
      { label: "Range Forward", value: "Range Forward" },
      { label: "Long Synthetic Future", value: "Long Synthetic Future" },
    ]
  },
  {
    group: "Bearish",
    templates: [
      { label: "Buy Put", value: "Buy Put" },
      { label: "Sell Call", value: "Sell Call" },
      { label: "Bear Call Spread", value: "Bear Call Spread" },
      { label: "Bear Put Spread", value: "Bear Put Spread" },
      { label: "Put Ratio Back Spread", value: "Put Ratio Back Spread" },
      { label: "Long Calendar with Puts", value: "Long Calendar with Puts" },
      { label: "Bear Condor", value: "Bear Condor" },
      { label: "Bear Butterfly", value: "Bear Butterfly" },
      { label: "Risk Reversal", value: "Risk Reversal" },
      { label: "Short Synthetic Future", value: "Short Synthetic Future" },
    ]
  },
  {
    group: "Neutral",
    templates: [
      { label: "Short Straddle", value: "Short Straddle" },
      { label: "Short Strangle", value: "Short Strangle" },
      { label: "Iron Butterfly", value: "Iron Butterfly" },
      { label: "Short Iron Condor", value: "Short Iron Condor" },
      { label: "Batman", value: "Batman" },
      { label: "Double Plateau", value: "Double Plateau" },
      { label: "Jade Lizard", value: "Jade Lizard" },
      { label: "Reverse Jade Lizard", value: "Reverse Jade Lizard" },
    ]
  },
  {
    group: "Others",
    templates: [
      { label: "Call Ratio Spread", value: "Call Ratio Spread" },
      { label: "Put Ratio Spread", value: "Put Ratio Spread" },
      { label: "Long Straddle", value: "Long Straddle" },
      { label: "Long Iron Butterfly", value: "Long Iron Butterfly" },
      { label: "Long Strangle", value: "Long Strangle" },
      { label: "Long Iron Condor", value: "Long Iron Condor" },
      { label: "Strip", value: "Strip" },
      { label: "Strap", value: "Strap" },
    ]
  }
];

export const formatTimestamp = (tsMicros) => {
	if (!tsMicros) return '-';
	return moment(tsMicros / 1000).format('HH:mm:ss.SSS');
};

export const getRowColorStyle = (color) => {
	if (!color || color === 'gray') return {};
	const colorMap = {
		'red': { backgroundColor: 'rgba(239, 68, 68, 0.1)' },
		'green': { backgroundColor: 'rgba(34, 197, 94, 0.1)' },
		'yellow': { backgroundColor: 'rgba(234, 179, 8, 0.1)' },
		'blue': { backgroundColor: 'rgba(59, 130, 246, 0.1)' },
		'orange': { backgroundColor: 'rgba(249, 115, 22, 0.1)' },
	};
	return colorMap[color.toLowerCase()] || {};
};
